import React, { useEffect, useState } from "react";
import { EMPTY_ITEM } from "../../constants/chargeConstants";
import "../../../../../css/Finance/Charges/ChargesEditorModal.css";

const emptyModifier = () => ({
  id: `modifier_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  label: "",
  amount: "",
  amountType: "fixed",
});

const ChargesEditorModal = ({
  open,
  mode,
  item,
  categoryName,
  saving,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState({
    ...EMPTY_ITEM,
    modifiers: [],
  });

  useEffect(() => {
    if (item) {
      setForm({
        ...EMPTY_ITEM,
        ...item,
        amount: item.amount ?? "",
        modifiers: (item.modifiers || []).map((modifier, index) => ({
          id:
            modifier.id ||
            `modifier_${Date.now()}_${index}_${Math.random()
              .toString(36)
              .slice(2, 8)}`,
          label: modifier.label || "",
          amount: modifier.amount ?? "",
          amountType: modifier.amountType || "fixed",
        })),
      });
    } else {
      setForm({
        ...EMPTY_ITEM,
        modifiers: [],
      });
    }
  }, [item, open]);

  if (!open) return null;

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleModifierChange = (index, key, value) => {
    setForm((prev) => {
      const nextModifiers = [...(prev.modifiers || [])];
      nextModifiers[index] = {
        ...nextModifiers[index],
        [key]: value,
      };

      return {
        ...prev,
        modifiers: nextModifiers,
      };
    });
  };

  const handleAddModifier = () => {
    setForm((prev) => ({
      ...prev,
      modifiers: [...(prev.modifiers || []), emptyModifier()],
    }));
  };

  const handleRemoveModifier = (index) => {
    setForm((prev) => ({
      ...prev,
      modifiers: (prev.modifiers || []).filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.name?.trim()) return;
    if (form.amount === "" || Number.isNaN(Number(form.amount))) return;

    const cleanedModifiers = (form.modifiers || [])
      .map((modifier) => ({
        id: modifier.id,
        label: modifier.label?.trim() || "",
        amount: Number(modifier.amount || 0),
        amountType: modifier.amountType || "fixed",
      }))
      .filter(
        (modifier) =>
          modifier.label &&
          !Number.isNaN(modifier.amount) &&
          Number(modifier.amount) >= 0,
      );

    onSave?.({
      ...form,
      amount: Number(form.amount),
      displayOrder: Number(form.displayOrder || 999),
      modifiers: cleanedModifiers,
    });
  };

  return (
    <div className="charges-editor-overlay">
      <div className="charges-editor-modal">
        <div className="charges-editor-header">
          <div>
            <h2 className="charges-editor-title">
              {mode === "edit" ? "Edit Charge" : "Add Charge"}
            </h2>
            <div className="charges-editor-category">{categoryName}</div>
          </div>

          <button
            type="button"
            className="charges-editor-close-btn"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <form className="charges-editor-form" onSubmit={handleSubmit}>
          <div className="charges-editor-field-wrap">
            <label>Name</label>
            <input
              className="charges-editor-input"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="e.g. Pension Transfer"
            />
          </div>

          <div className="charges-editor-field-wrap">
            <label>Description</label>
            <textarea
              className="charges-editor-textarea"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Optional"
            />
          </div>

          <div className="charges-editor-row">
            <div className="charges-editor-field-wrap">
              <label>Amount</label>
              <input
                type="number"
                className="charges-editor-input"
                value={form.amount}
                onChange={(e) => handleChange("amount", e.target.value)}
                placeholder="0"
              />
            </div>

            <div className="charges-editor-field-wrap">
              <label>Amount Prefix</label>
              <select
                className="charges-editor-select"
                value={form.amountPrefix}
                onChange={(e) => handleChange("amountPrefix", e.target.value)}
              >
                <option value="">None</option>
                <option value="+">+</option>
              </select>
            </div>
          </div>

          <div className="charges-editor-row">
            <div className="charges-editor-field-wrap">
              <label>Display Order</label>
              <input
                type="number"
                className="charges-editor-input"
                value={form.displayOrder}
                onChange={(e) => handleChange("displayOrder", e.target.value)}
              />
            </div>

            <div className="charges-editor-field-wrap">
              <label>Status</label>
              <select
                className="charges-editor-select"
                value={String(form.isActive)}
                onChange={(e) =>
                  handleChange("isActive", e.target.value === "true")
                }
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          <div className="charges-editor-modifiers-section">
            <div className="charges-editor-modifiers-header">
              <h3 className="charges-editor-modifiers-title">
                Additional Charges
              </h3>

              <button
                type="button"
                className="charges-editor-add-modifier-btn"
                onClick={handleAddModifier}
              >
                + Add 
              </button>
            </div>

            {!form.modifiers?.length ? (
              <div className="charges-editor-modifiers-empty">
                No additional charges added yet.
              </div>
            ) : (
              <div className="charges-editor-modifiers-list">
                {form.modifiers.map((modifier, index) => (
                  <div
                    key={modifier.id || index}
                    className="charges-editor-modifier-card"
                  >
                    <div className="charges-editor-row">
                      <div className="charges-editor-field-wrap">
                        <label>Name</label>
                        <input
                          className="charges-editor-input"
                          value={modifier.label}
                          onChange={(e) =>
                            handleModifierChange(index, "label", e.target.value)
                          }
                          placeholder="e.g. Additional ceding scheme"
                        />
                      </div>

                      <div className="charges-editor-field-wrap">
                        <label>Amount</label>
                        <input
                          type="number"
                          className="charges-editor-input"
                          value={modifier.amount}
                          onChange={(e) =>
                            handleModifierChange(
                              index,
                              "amount",
                              e.target.value,
                            )
                          }
                          placeholder="0"
                        />
                      </div>
                    </div>

                    <div className="charges-editor-row">
                      <div className="charges-editor-field-wrap">
                        <label>Amount Type</label>
                        <select
                          className="charges-editor-select"
                          value={modifier.amountType}
                          onChange={(e) =>
                            handleModifierChange(
                              index,
                              "amountType",
                              e.target.value,
                            )
                          }
                        >
                          <option value="fixed">Fixed</option>
                        </select>
                      </div>

                      <div className="charges-editor-field-wrap charges-editor-modifier-remove-wrap">
                        <label>&nbsp;</label>
                        <button
                          type="button"
                          className="charges-editor-remove-modifier-btn"
                          onClick={() => handleRemoveModifier(index)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="charges-editor-btn-row">
            <button
              type="button"
              className="charges-editor-cancel-btn"
              onClick={onClose}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="charges-editor-save-btn"
              disabled={saving}
              style={saving ? { opacity: 0.7 } : {}}
            >
              {saving
                ? "Saving..."
                : mode === "edit"
                  ? "Save changes"
                  : "Add charge"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChargesEditorModal;
